import moment from 'moment';

/**
 * @param  {string} value Date as string, eg. 2020-12-31 23:59:59. Please refer to https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#Several_ways_to_create_a_Date_object for further information
 * @param  {object} opts Date format, please refer to https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/toLocaleString#Using_options for further information
 * @return {string} Full datetime/date/time as string based on the given opts
 */
export const formatDateTime = (
  value,
  opts = {
    weekday: '',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  },
  isNeedFormatting = true,
) => {
  const date = isNeedFormatting ? value.replace(/\s/, 'T') : value;
  return new Date(date).toLocaleString('EN-GB', opts);
};

/**
 * @param  {string} value Date as string, eg. 2020-12-31 23:59:59. Please refer to https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#Several_ways_to_create_a_Date_object for further information
 * @return {string} Date (Day & Month) as string, eg. 31 Dec
 */
export const formatDateMonth = (value, isLong = false) => {
  return formatDateTime(value, {
    day: 'numeric',
    month: isLong ? 'long' : 'short',
  });
};

/**
 * @param  {string} value Date as string, eg. 2020-12-31 23:59:59. Please refer to https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#Several_ways_to_create_a_Date_object for further information
 * @return {string} Full date as string, eg. 31 Dec 2020
 */
export const formatDateMonthYear = (value, isLong = false) => {
  return formatDateTime(value, {
    day: 'numeric',
    month: isLong ? 'long' : 'short',
    year: 'numeric',
  });
};

/**
 * @param  {string} value Date as string, eg. 2020-12-31 23:59:59. Please refer to https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#Several_ways_to_create_a_Date_object for further information
 * @return {string} Full date as string, eg. Wednesday, 31 Dec 2020
 */
export const formatDayDateMonthYear = value => {
  return formatDateTime(value, {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * @param  {string} value Date as string, eg. 2020-12-31 23:59:59. Please refer to https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#Several_ways_to_create_a_Date_object for further information
 * @return {string} Full date as string, eg. 1 - 31 Nov 2020 / 1 Nov - 31 Dec 2020 /  1 Nov 2020 - 31 Jan 2021
 */
export const formatDateRange = (startVal, endVal, isLong = false) => {
  const start = startVal || '';
  const end = endVal || '';

  const startDate = new Date(start.replace(/\s/, 'T'));
  const endDate = new Date(end.replace(/\s/, 'T'));

  const sameDate = startDate.getDate() === endDate.getDate();
  const sameYear = startDate.getFullYear() === endDate.getFullYear();
  const sameMonth = startDate.getMonth() === endDate.getMonth();

  if (sameYear && sameMonth && sameDate) {
    return `${formatDateMonthYear(start, isLong)}`;
  }
  if (sameYear && sameMonth) {
    return `${startDate.getDate()} - ${formatDateMonthYear(end, isLong)}`;
  }
  if (sameYear) {
    return `${formatDateMonth(start, isLong)} - ${formatDateMonthYear(end, isLong)}`;
  }
  return `${formatDateMonthYear(start, isLong)} - ${formatDateMonthYear(end, isLong)}`;
};

/**
 * @param  {string} value Date as string, eg. 2020-12-31 23:59:59. Please refer to https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#Several_ways_to_create_a_Date_object for further information
 * @param  {boolean} hour12 Set true if you want to display it as a 12-hours format
 * @return {string} Hour and minute of the given value accoridng to your configuration, eg. 12:59 PM or 23:59
 */
export const formatHour = (value, hour12 = true) => {
  return formatDateTime(value, {
    hour: '2-digit',
    minute: '2-digit',
    hour12,
  }).toUpperCase();
};

/**
 * @param  {string} value Date as string, eg. 2020-12-31 23:59:59. Please refer to https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date#Several_ways_to_create_a_Date_object for further information
 * @return {string} Full date, hour and minute of the given value accoridng to your configuration, eg. 25 Feb 2021, 02:06 PM
 */
export const formatDateHour = (value, type) => {
  if (type === 'at') return `${formatDateMonthYear(value)} at ${value.split(' ')[1]}`;
  return `${formatDateMonthYear(value)}, ${formatHour(value)}`;
};

/**
 * Format date of the given date according to the given format
 *
 * @param  {?string}      [str=null]              Valid date, eg. 1995-12-17T03:24:00
 * @param  {!string}      [format='DD MM YYYY']   Valid moment.js format, https://momentjs.com/docs/#/displaying/format/
 * @param  {boolean}      [legacy=true]           Legacy mode, to handle non-recognized RFC2822 or ISO format. Can only support "DD MM YYYY", "DD MMM YYYY", and "YYYY-MM-DD".
 *
 * @returns {string}  Formatted date
 */
export function formatDate(str = null, format = 'DD MM YYYY', legacy = false) {
  let mom = moment();

  if (str !== null) {
    if (legacy) {
      if (typeof str === 'number') mom = moment(new Date(str).toISOString());
      else mom = moment(str, ['DD MM YYYY', 'DD MMM YYYY', 'YYYY-MM-DD', 'MMM YYYY']);
    } else mom = moment(str);
  }

  return mom.format(format);
}

/**
 * @param  {string} value Date as string, eg. Thu Dec 31 2020 07:00:00 GMT+0700 (Western Indonesia Time)
 * @return {string} Full date, eg. 2020-12-31
 */
export const formatDateApi = value => formatDate(value, 'YYYY-MM-DD', true);

/**
 * @param  {string} value Valid date, eg. Thu Dec 31 2020 07:00:00 GMT+0700 (Western Indonesia Time)
 * @return {string} Date hour, eg. 2020-12-31 23:59:59
 */
export const formatDateApiHour = value => formatDate(value, 'YYYY-MM-DD HH:mm:ss', true);

/**
 * Check if givens date was greater or equal with date
 *
 * @param  {date1}      [date]              Valid date, eg. 1995-12-17T03:24:00
 * @param  {date2}      [date]              Valid date, eg. 1995-12-17T03:24:00
 *
 * @returns {boolean}  true/false
 */
export const isSameOrAfter = (date1, date2) => {
  const formatDate1 = moment(date1);
  const formatDate2 = moment(date2);
  return formatDate1.diff(formatDate2) >= 0;
};

/**
 * Check if givens date was less or equal with date
 *
 * @param  {date1}      [date]              Valid date, eg. 1995-12-17T03:24:00
 * @param  {date2}      [date]              Valid date, eg. 1995-12-17T03:24:00
 *
 * @returns {boolean}  true/false
 */
export const isSameOrBefore = (date1, date2) => {
  const formatDate1 = moment(date1).startOf('day');
  const formatDate2 = moment(date2).startOf('day');
  return formatDate1.isSameOrBefore(formatDate2);
};

/**
 * Check if givens date was greater or equal with date
 *
 * @param  {date}      [date]              Valid date, eg. 1995-12-17T03:24:00
 * @param  {startPeriod}      [date]              Valid date, eg. 1995-12-17T03:24:00
 * @param  {endPeriod}      [date]              Valid date, eg. 1995-12-17T03:24:00
 *
 * @returns {boolean}  true/false
 */
export const isOnPeriod = (date, start, end) => {
  const dateVal = moment(date);
  const startVal = moment(start).set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
  const endVal = moment(end).set({ hour: 23, minute: 59, second: 59, millisecond: 59 });
  return dateVal.isBetween(startVal, endVal, null, '[]');
};

/**
 * Check if givens date was greater or equal with date
 *
 * @param  {date1}      [date]              Valid date, eg. 1995-12-17T03:24:00
 * @param  {date2}      [date]              Valid date, eg. 1995-12-17T03:24:00
 *
 * @returns {boolean}  true/false
 */
export const isMoreThanAYear = (date1, date2) => {
  const formatDate1 = moment(date1);
  const formatDate2 = moment(date2);
  return formatDate2.diff(formatDate1, 'years', true) >= 1;
};

/**
 * Count days between givens date
 *
 * @param  {date1}      [date]              Valid date, eg. 1995-12-01
 * @param  {date2}      [date]              Valid date, eg. 1995-12-03
 *
 * @returns {string}  2
 */
export const countDays = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  return Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
};

/**
 * Get date only with its ordinal suffix
 *
 * @param  {date1}      [date]              Valid date, eg. 1995-12-11
 *
 * @returns {string}  11th
 */
export const formatOrdinalDate = date => {
  const dateOnly = new Date(date).getDate();

  const i = dateOnly % 10;
  let suffix = 'th';
  if (!(dateOnly >= 4 && dateOnly <= 20)) {
    if (i === 1) suffix = 'st';
    if (i === 2) suffix = 'nd';
    if (i === 3) suffix = 'rd';
  }
  return dateOnly + suffix;
};

/**
 * Get date & month with its ordinal suffix
 *
 * @param  {date1}      [date]              Valid date, eg. 1995-12-1
 *
 * @returns {string}  December 1st
 */
export const formatOrdinalDateMonth = date => {
  const month = formatDateTime(date, { month: 'long' }, false);
  return `${month} ${formatOrdinalDate(date)}`;
};
